import { touchQueueBucket, type TouchQueueBucket } from "./schedule";
import {
    isLeadEmailStatus,
    isLeadStatus,
    isTouchReplyKind,
    type Lead,
    type LeadEmailStatus,
    type LeadStatus,
    type Touch,
    type TouchKind,
} from "./types";

export const NO_TEAM_FILTER = "none";

export type ReplyOutcomeFilter = "positive" | "negative";

export const REPLY_OUTCOME_FILTER_OPTIONS: { value: ReplyOutcomeFilter; label: string }[] = [
    { value: "positive", label: "Positive reply" },
    { value: "negative", label: "Negative reply" },
];

export function isReplyOutcomeFilter(value: string): value is ReplyOutcomeFilter {
    return value === "positive" || value === "negative";
}

export interface LeadListFilters {
    company?: string;
    team?: string;
    status?: LeadStatus;
    email?: LeadEmailStatus;
    reply?: ReplyOutcomeFilter;
}

export interface TouchListFilters {
    lead?: string;
    company?: string;
    kind?: TouchKind;
    queue?: TouchQueueBucket;
    reply?: ReplyOutcomeFilter;
}

export interface CompanyListFilters {
    team?: string;
}

export function sameFilterText(left: string, right: string): boolean {
    return left.localeCompare(right, undefined, { sensitivity: "base" }) === 0;
}

export function compactFilters<T extends Record<string, string | undefined>>(filters: T): T {
    return Object.fromEntries(Object.entries(filters).filter(([, value]) => Boolean(value))) as T;
}

export function hasLeadFilters(filters: LeadListFilters): boolean {
    return leadFilterCount(filters) > 0;
}

export function hasTouchFilters(filters: TouchListFilters): boolean {
    return touchFilterCount(filters) > 0;
}

export function hasCompanyFilters(filters: CompanyListFilters): boolean {
    return companyFilterCount(filters) > 0;
}

export function leadFilterCount(filters: LeadListFilters): number {
    return [filters.company, filters.team, filters.status, filters.email, filters.reply].filter(Boolean).length;
}

export function touchFilterCount(filters: TouchListFilters): number {
    return [filters.lead, filters.company, filters.kind, filters.queue, filters.reply].filter(Boolean).length;
}

export function companyFilterCount(filters: CompanyListFilters): number {
    return filters.team ? 1 : 0;
}

export function parseLeadListSearch(search: Record<string, unknown>): LeadListFilters {
    return compactFilters({
        company: typeof search.company === "string" ? search.company : undefined,
        team: typeof search.team === "string" ? search.team : undefined,
        status: typeof search.status === "string" && isLeadStatus(search.status) ? search.status : undefined,
        email: typeof search.email === "string" && isLeadEmailStatus(search.email) ? search.email : undefined,
        reply: typeof search.reply === "string" && isReplyOutcomeFilter(search.reply) ? search.reply : undefined,
    });
}

export function parseTouchListSearch(search: Record<string, unknown>): TouchListFilters {
    const kind = search.kind;
    const queue = search.queue;
    return compactFilters({
        lead: typeof search.lead === "string" ? search.lead : undefined,
        company: typeof search.company === "string" ? search.company : undefined,
        kind:
            kind === "connect" ||
            kind === "email" ||
            kind === "linkedin" ||
            kind === "email_reply" ||
            kind === "linkedin_reply"
                ? kind
                : undefined,
        queue:
            queue === "overdue" || queue === "undated" || queue === "today" || queue === "upcoming" || queue === "sent"
                ? queue
                : undefined,
        reply: typeof search.reply === "string" && isReplyOutcomeFilter(search.reply) ? search.reply : undefined,
    });
}

export function leadHasReplyOutcome(lead: Lead, outcome: ReplyOutcomeFilter): boolean {
    if (outcome === "positive" && lead.status === "positive_response") return true;
    if (outcome === "negative" && lead.status === "negative_response") return true;
    return lead.touches.some((touch) => isTouchReplyKind(touch.kind) && touch.status === outcome);
}

export function touchIsReplyOutcome(touch: Touch, outcome: ReplyOutcomeFilter): boolean {
    return isTouchReplyKind(touch.kind) && touch.status === outcome;
}

export function leadMatchesFilters(lead: Lead, filters: LeadListFilters): boolean {
    if (filters.company && !sameFilterText(lead.company, filters.company)) return false;
    if (filters.team === NO_TEAM_FILTER) {
        if (lead.team.trim()) return false;
    } else if (filters.team && !sameFilterText(lead.team, filters.team)) {
        return false;
    }
    if (filters.status && lead.status !== filters.status) return false;
    if (filters.email && lead.email_status !== filters.email) return false;
    if (filters.reply && !leadHasReplyOutcome(lead, filters.reply)) return false;
    return true;
}

export function touchMatchesFilters(
    row: { lead_id: string; company: string; touch: Touch },
    filters: TouchListFilters,
    today?: string
): boolean {
    if (filters.lead && row.lead_id !== filters.lead) return false;
    if (filters.company && !sameFilterText(row.company, filters.company)) return false;
    if (filters.kind && row.touch.kind !== filters.kind) return false;
    if (filters.queue && touchQueueBucket(row.touch, today) !== filters.queue) return false;
    if (filters.reply && !touchIsReplyOutcome(row.touch, filters.reply)) return false;
    return true;
}

export function companyMatchesFilters(company: { teams: string[] }, filters: CompanyListFilters): boolean {
    if (!filters.team) return true;
    return company.teams.some((team) => sameFilterText(team, filters.team!));
}
